import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchSecurityOverview, type SecurityOverview } from '@/lib/adapters'
import { ErrorState, Skeleton } from '@/components/States'

/** Epoch ms (string|number) → qisqa lokal sana-vaqt. */
function fmtTime(v: string | number): string {
  const n = typeof v === 'number' ? v : Number(v)
  if (!Number.isFinite(n) || n <= 0) return '—'
  return new Date(n).toLocaleString()
}

/**
 * A2.08 — Xavfsizlik dashboard'i. Audit jurnali + four-eyes navbati + xodim
 * holatini bitta ko'rinishда jamlaydi (`/admin/audit` + `/admin/four-eyes` +
 * `/admin/staff` ustidan — JONLI, alohida agregatsiya-endpoint KUTMAYDI).
 */
export function SecurityDashboard() {
  const t = useT()
  const [data, setData] = useState<SecurityOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchSecurityOverview(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />

  const maxAction = data ? Math.max(1, ...data.audit_actions.map((a) => a.count)) : 1

  return (
    <>
      <PageHeader title={t.secDash.title} screenId="A2.08" description={t.secDash.note} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          <KPILink
            to="/security/four-eyes"
            label={t.secDash.fourEyesOpen}
            value={data?.four_eyes_open}
            loading={loading}
            warn={(data?.four_eyes_open ?? 0) > 0}
          />
          <KPILink
            to="/staff"
            label={t.secDash.staffActive}
            value={data ? `${data.staff_active}/${data.staff_total}` : undefined}
            loading={loading}
          />
          <KPI label={t.secDash.auditWindow} value={data?.recent_audit.length} loading={loading} />
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <Panel title={t.secDash.topActions}>
            {loading ? (
              <BarsSkeleton />
            ) : !data || !data.audit_actions.length ? (
              <Empty t={t.dash.noData} />
            ) : (
              <div className="space-y-1.5">
                {data.audit_actions.map((a) => (
                  <div key={a.key} className="flex items-center gap-3">
                    <span className="w-40 shrink-0 truncate font-mono text-xs text-secondary">{a.key}</span>
                    <div className="relative h-5 flex-1 overflow-hidden rounded bg-surface-3">
                      <div
                        className="h-full rounded bg-accent/70"
                        style={{ width: `${Math.max(2, (a.count / maxAction) * 100)}%` }}
                      />
                    </div>
                    <span className="w-12 shrink-0 text-right font-mono text-sm tabular text-primary">
                      {a.count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title={t.secDash.recentAudit}>
            {loading ? (
              <BarsSkeleton />
            ) : !data || !data.recent_audit.length ? (
              <Empty t={t.dash.noData} />
            ) : (
              <div className="divide-y divide-border-subtle">
                {data.recent_audit.map((a, i) => (
                  <div key={a.id ?? i} className="flex items-center justify-between gap-2 py-1.5 text-sm">
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-mono text-xs text-accent">{a.action}</span>
                      {a.target && <span className="ml-1.5 truncate text-tertiary">{a.target}</span>}
                    </span>
                    <span className="shrink-0 text-xs text-secondary">{a.admin ?? '—'}</span>
                    <span className="w-32 shrink-0 text-right font-mono text-xs text-tertiary">
                      {fmtTime(a.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2 border-t border-border-subtle pt-2 text-right">
              <Link to="/security/audit" className="text-xs text-accent hover:underline">
                {t.secDash.fullLog} →
              </Link>
            </div>
          </Panel>
        </div>
      </div>
    </>
  )
}

function KPI({
  label,
  value,
  loading,
}: {
  label: string
  value: number | undefined
  loading: boolean
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-7 w-16" />
      ) : (
        <div className="mt-0.5 font-mono text-2xl tabular text-primary">{value?.toLocaleString() ?? '—'}</div>
      )}
    </div>
  )
}

function KPILink({
  to,
  label,
  value,
  loading,
  warn,
}: {
  to: string
  label: string
  value: number | string | undefined
  loading: boolean
  warn?: boolean
}) {
  return (
    <Link
      to={to}
      className="rounded-lg border border-border-default bg-surface-2 p-3 transition-colors hover:border-border-strong hover:bg-surface-3"
    >
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-7 w-16" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${warn ? 'text-warning' : 'text-primary'}`}>
          {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
        </div>
      )}
    </Link>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}

function BarsSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-5 w-full" />
      ))}
    </div>
  )
}

function Empty({ t }: { t: string }) {
  return <div className="py-6 text-center text-sm text-tertiary">{t}</div>
}
